/**
 * Send Native Auth to Mobile WebView
 *
 * Exchanges a signed registration completion token for native mobile tokens
 * and sends NATIVE_AUTH message to the mobile app.
 * Used after web registration to authenticate the user in the native app.
 *
 * IMPORTANT: This now waits for confirmation from the native app before returning,
 * preventing the race condition where web proceeds to bank connection before
 * native auth is saved.
 */

/** Timeout for waiting for native auth confirmation (ms) */
const NATIVE_AUTH_CONFIRMATION_TIMEOUT = 5000;

interface NativeAuthUser {
  id: string;
  email: string;
  name: string;
  phone?: string;
  image?: string;
  emailVerifiedAt: string | null;
  phoneVerifiedAt: string | null;
  createdAt: string;
}

interface NativeAuthResponse {
  success: boolean;
  data?: {
    accessToken: string;
    refreshToken?: string;
    user: NativeAuthUser;
  };
  error?: {
    code: string;
    message: string;
  };
}

interface NativeAuthConfirmationDetail {
  success: boolean;
  userId?: string;
}

/**
 * Check if we're in a mobile WebView
 */
export function isInMobileWebView(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    typeof (window as any).ReactNativeWebView !== 'undefined' &&
    typeof (window as any).ReactNativeWebView.postMessage === 'function'
  );
}

/**
 * Send a message to the mobile WebView
 */
function postMessageToMobile(message: Record<string, unknown>): void {
  if (!isInMobileWebView()) return;
  (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
}

/**
 * Exchange a registration completion token for native mobile tokens
 */
async function exchangeRegistrationToken(
  registrationToken: string,
): Promise<NativeAuthResponse> {
  try {
    const response = await fetch('/api/mobile/auth/post-registration-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ registrationToken }),
    });

    return await response.json();
  } catch (error) {
    console.error('[sendNativeAuth] Failed to exchange token:', error);
    return {
      success: false,
      error: {
        code: 'FETCH_ERROR',
        message: 'Failed to fetch native tokens',
      },
    };
  }
}

/**
 * Wait for native auth confirmation from the mobile app.
 * The native app dispatches a 'nativeAuthConfirmed' CustomEvent after successfully saving auth.
 *
 * @returns Promise that resolves with confirmation details or resolves with success on timeout
 */
function waitForNativeAuthConfirmation(): Promise<NativeAuthConfirmationDetail> {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      window.removeEventListener('nativeAuthConfirmed', handleConfirmation);
      console.warn(
        '[sendNativeAuth] Native auth confirmation timed out after',
        NATIVE_AUTH_CONFIRMATION_TIMEOUT,
        'ms - proceeding anyway',
      );
      // Resolve with success anyway - the native side may have completed
      // even if we didn't receive the confirmation (e.g., if navigation happened too fast)
      resolve({ success: true });
    }, NATIVE_AUTH_CONFIRMATION_TIMEOUT);

    const handleConfirmation = (event: Event) => {
      clearTimeout(timeoutId);
      window.removeEventListener('nativeAuthConfirmed', handleConfirmation);

      const customEvent = event as CustomEvent<NativeAuthConfirmationDetail>;
      const detail = customEvent.detail;
      resolve(detail);
    };

    window.addEventListener('nativeAuthConfirmed', handleConfirmation);
  });
}

/**
 * Send native auth tokens to mobile app after registration
 *
 * @param registrationToken - The signed registration completion token from the server
 * @returns true if tokens were sent successfully and confirmed by native, false otherwise
 */
export async function sendNativeAuthToMobile(
  registrationToken: string,
): Promise<boolean> {
  // Only proceed if in WebView
  if (!isInMobileWebView()) {
    return false;
  }

  if (!registrationToken) {
    console.error('[sendNativeAuth] No registration token provided');
    return false;
  }

  // Exchange the registration token for native mobile tokens
  const response = await exchangeRegistrationToken(registrationToken);

  if (!response.success || !response.data) {
    console.error('[sendNativeAuth] Failed to exchange token:', response.error);
    return false;
  }

  // Send NATIVE_AUTH message to mobile
  postMessageToMobile({
    type: 'NATIVE_AUTH',
    accessToken: response.data.accessToken,
    refreshToken: response.data.refreshToken,
    user: response.data.user,
  });

  // Wait for confirmation from native side before returning
  // This ensures the native app has saved the auth before web proceeds to next step
  const confirmation = await waitForNativeAuthConfirmation();

  if (confirmation.success) {
    return true;
  } else {
    console.warn(
      '[sendNativeAuth] Native auth confirmation indicated failure, but tokens were sent',
    );
    // Still return true since the tokens were sent - the native side may still work
    return true;
  }
}
